<script lang="ts">
  import Plus from '@lucide/svelte/icons/plus';
  import { onDestroy, untrack } from 'svelte';

  import { getServices } from '../../app/context';
  import { filterDoc, passingKinks, type VisibleCategory } from '../../model/filter';
  import { splitColumns } from '../../render/columns';
  import type { Category, Kink } from '../../model/types';
  import { strings } from '../../strings';
  import Button from '../../ui/Button.svelte';
  import { ColumnCount } from '../../ui/columns.svelte';
  import { ReorderState } from '../../ui/reorder.svelte';
  import CategoryDialog from '../edit/CategoryDialog.svelte';
  import CommentDialog from '../kink/CommentDialog.svelte';
  import CategoryCard from './CategoryCard.svelte';

  const { store, settings, view } = getServices();

  const filter = $derived(settings.value.filter);
  // "Has comment" keeps the kinks that passed when it (or the search) last
  // changed, so removing a comment dims the row instead of making it vanish.
  const snapshotKey = $derived(`${filter}\u0000${view.query}`);
  const keep = $derived.by(() => {
    if (snapshotKey === '' || filter !== 'commented') return undefined;
    const current = filter;
    return untrack(() => passingKinks(store.doc, current));
  });
  const visible = $derived(filterDoc(store.doc, { query: view.query, filter, keep }));
  const searching = $derived(view.query.trim() !== '');
  const filtering = $derived(searching || settings.value.filter !== 'all');
  const categoryReorder = new ReorderState();

  // Masonry in JS: CSS multi-column layout re-balances on every change, which
  // made WebKit take seconds per edit with ~120 kinks (see docs/decisions.md).
  const columnCount = new ColumnCount();
  let measured: Record<string, number> = $state({});
  const observer =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver((entries) => {
          for (const entry of entries) {
            const id =
              entry.target instanceof HTMLElement ? entry.target.dataset.cardId : undefined;
            const height = Math.round(
              entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height,
            );
            if (id !== undefined && measured[id] !== height) measured[id] = height;
          }
        });
  onDestroy(() => {
    observer?.disconnect();
    columnCount.destroy();
  });

  function observe(element: HTMLElement) {
    observer?.observe(element);
    return () => {
      observer?.unobserve(element);
    };
  }

  /** Rough height until a card has been measured. */
  function estimate(entry: VisibleCategory): number {
    if (!entry.expanded && !view.editMode) return 72;
    const row = entry.category.roles.length > 1 ? 93 : 77;
    return 72 + entry.kinks.length * row + (view.editMode ? 56 : 0);
  }

  const columns = $derived.by(() => {
    const heights = visible.map((entry) => measured[entry.category.id] ?? estimate(entry));
    return splitColumns(heights, columnCount.value).map((column) =>
      column.flatMap((i) => visible[i] ?? []),
    );
  });
  const lastColumn = $derived(columns.findLastIndex((column) => column.length > 0));
  const reorder = $derived(view.editMode && !filtering ? categoryReorder : null);

  let commenting: { categoryId: string; kinkId: string; trigger: HTMLElement } | null =
    $state(null);
  // Resolve against the live document so the dialog always shows current data.
  const target = $derived.by((): { category: Category; kink: Kink } | null => {
    if (!commenting) return null;
    const { categoryId, kinkId } = commenting;
    const category = store.doc.categories.find((c) => c.id === categoryId);
    const kink = category?.kinks.find((k) => k.id === kinkId);
    return category && kink ? { category, kink } : null;
  });

  let editing: { category: Category | null; trigger: HTMLElement | null } | null = $state(null);
  let categoryDialogOpen = $state(false);

  function openCategoryDialog(category: Category | null, trigger: HTMLElement | null) {
    editing = { category, trigger };
    categoryDialogOpen = true;
  }
</script>

{#if visible.length === 0 && !view.editMode}
  <div class="flex flex-col items-center gap-3 py-16 text-center">
    <p class="text-ink-muted">{strings.noMatches}</p>
    <Button
      onclick={() => {
        view.query = '';
        settings.update({ filter: 'all' });
      }}
    >
      {strings.clearFilters}
    </Button>
  </div>
{:else}
  <div class="flex items-start gap-3">
    {#each columns as column, c (c)}
      <div class="flex min-w-0 flex-1 flex-col">
        {#each column as entry (entry.category.id)}
          <div {@attach observe} data-card-id={entry.category.id}>
            <CategoryCard
              {entry}
              collapsible={!searching}
              {reorder}
              oncomment={(kink: Kink, trigger: HTMLElement) => {
                commenting = { categoryId: entry.category.id, kinkId: kink.id, trigger };
              }}
              onedit={(category: Category, trigger: HTMLElement) => {
                openCategoryDialog(category, trigger);
              }}
            />
          </div>
        {/each}
        {#if view.editMode && c === Math.max(lastColumn, 0)}
          <button
            type="button"
            class="mb-3 flex w-full items-center justify-center gap-2 rounded-card border-2 border-dashed border-line-strong/60 py-8 text-sm font-medium text-ink-muted hover:border-accent hover:text-accent"
            onclick={(event) => {
              openCategoryDialog(null, event.currentTarget);
            }}
          >
            <Plus size={18} aria-hidden="true" />
            {strings.addCategory}
          </button>
        {/if}
      </div>
    {/each}
  </div>
{/if}

<CommentDialog
  {target}
  trigger={commenting?.trigger}
  onclose={() => {
    commenting = null;
  }}
/>

<CategoryDialog
  bind:open={categoryDialogOpen}
  category={editing?.category ?? null}
  returnFocusTo={editing?.trigger}
  onclose={() => {
    editing = null;
  }}
/>
